// Settings → "Members" and "Leave or delete": who is in the workspace, who is
// invited (the link is e-mailed when the server has mail, else shared by hand), and how to leave
// or delete it. Management actions are for owners and administrators; the
// API enforces that too, the buttons just hide.

import { useState, type FormEvent } from 'react'
import type { CreateInvitationResponse, InvitationSummary } from '@goblic/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  invitationsApi,
  workspacesApi,
  workspaceUsersApi,
  type MemberRole,
  type WorkspaceMember,
} from '../lib/api-client'
import { switchToToken, useAuth } from '../lib/auth'
import { formatDateTime, humanize } from '../lib/format'
import { useDialogs } from './dialogs'
import { Dialog, ErrorNote, Field, LoadingRows, Notice, Panel } from './ui'

const ALL_ROLES: MemberRole[] = [
  'workspace_owner',
  'administrator',
  'operator',
  'approver',
  'viewer',
]

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : `${fallback}: ${(error as Error).message}`
}

function RoleCheckboxes({
  value,
  onChange,
  allowed,
  idPrefix,
}: {
  value: MemberRole[]
  onChange: (roles: MemberRole[]) => void
  allowed: MemberRole[]
  idPrefix: string
}) {
  return (
    <div className="space-y-1.5">
      {allowed.map((role) => (
        <label key={role} className="flex items-center gap-2 text-base text-ink" htmlFor={`${idPrefix}-${role}`}>
          <input
            id={`${idPrefix}-${role}`}
            type="checkbox"
            checked={value.includes(role)}
            onChange={(event) =>
              onChange(event.target.checked ? [...value, role] : value.filter((r) => r !== role))
            }
          />
          {humanize(role)}
        </label>
      ))}
    </div>
  )
}

/** After creating or resending: whether the e-mail went out, plus the link as a fallback. */
function InviteResult({ result, onDone }: { result: CreateInvitationResponse; onDone: () => void }) {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(result.url)
      setCopied(true)
      setCopyFailed(false)
    } catch {
      // No clipboard on plain-http origins: select the link so Ctrl+C works.
      setCopied(false)
      setCopyFailed(true)
      document.getElementById('invite-link')?.focus()
      ;(document.getElementById('invite-link') as HTMLInputElement | null)?.select()
    }
  }

  return (
    <div className="space-y-4">
      {result.emailSent ? (
        <Notice tone="success">Invitation e-mailed to {result.invitation.email}.</Notice>
      ) : result.emailError ? (
        <Notice tone="error">{result.emailError}</Notice>
      ) : null}
      <Field label="Invitation link" htmlFor="invite-link">
        <input
          id="invite-link"
          data-autofocus
          readOnly
          className="field-input"
          value={result.url}
          onFocus={(event) => event.currentTarget.select()}
        />
      </Field>
      <p className="text-base text-ink-2">
        {result.emailSent ? 'You can also share this link yourself.' : 'Share this link with them.'} It works
        once and expires in 7 days.
      </p>
      {copyFailed ? <Notice tone="error">Copy failed. The link is selected: copy it manually.</Notice> : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onDone}>
          Done
        </button>
        <button type="button" className="btn-primary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  )
}

function InviteDialog({
  open,
  onClose,
  workspaceId,
  isOwner,
  mailEnabled,
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  isOwner: boolean
  mailEnabled: boolean
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [roles, setRoles] = useState<MemberRole[]>(['operator'])
  const [sendEmail, setSendEmail] = useState(true)
  const [created, setCreated] = useState<CreateInvitationResponse | null>(null)
  const allowed = isOwner ? ALL_ROLES : ALL_ROLES.filter((r) => r !== 'workspace_owner')

  const create = useMutation({
    mutationFn: () =>
      invitationsApi.create(workspaceId, {
        email: email.trim(),
        roles,
        ...(mailEnabled ? { sendEmail } : {}),
      }),
    onSuccess: (result) => {
      setCreated(result)
      void queryClient.invalidateQueries({ queryKey: ['workspace-invitations', workspaceId] })
    },
  })

  function close(): void {
    if (create.isPending) return
    create.reset()
    setEmail('')
    setRoles(['operator'])
    setCreated(null)
    setSendEmail(true)
    onClose()
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    create.mutate()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      size="md"
      title="Invite people"
      description="They get a link to join this workspace with the roles you pick."
    >
      {created ? (
        <InviteResult result={created} onDone={close} />
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Field label="Email" htmlFor="invite-email">
            <input
              id="invite-email"
              data-autofocus
              type="email"
              className="field-input"
              value={email}
              required
              autoCapitalize="none"
              spellCheck={false}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label="Roles" hint="At least one.">
            <RoleCheckboxes value={roles} onChange={setRoles} allowed={allowed} idPrefix="invite-role" />
          </Field>
          {mailEnabled ? (
            <label className="flex items-start gap-2 text-base text-ink-2" htmlFor="invite-send-email">
              <input
                id="invite-send-email"
                type="checkbox"
                className="mt-1"
                checked={sendEmail}
                onChange={(event) => setSendEmail(event.target.checked)}
              />
              <span>We’ll e-mail them the link.</span>
            </label>
          ) : null}
          {create.isError ? (
            <Notice tone="error">{errorMessage(create.error, 'Couldn’t create the invitation')}</Notice>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={close} disabled={create.isPending}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={create.isPending || email.trim() === '' || roles.length === 0}
            >
              {create.isPending ? 'Creating…' : 'Create invitation'}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  )
}

function EditRolesDialog({
  member,
  workspaceId,
  isOwner,
  onClose,
}: {
  member: WorkspaceMember | null
  workspaceId: string
  isOwner: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [roles, setRoles] = useState<MemberRole[]>(member?.roles ?? [])
  const [lastMember, setLastMember] = useState(member)
  if (member !== lastMember) {
    setLastMember(member)
    if (member) setRoles(member.roles)
  }
  const save = useMutation({
    mutationFn: () => workspaceUsersApi.updateRoles(workspaceId, member!.userId, roles),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] })
      onClose()
    },
  })
  // Non-owners never get to this dialog for an owner (the row hides the button).
  const allowed = isOwner ? ALL_ROLES : ALL_ROLES.filter((r) => r !== 'workspace_owner')

  function close(): void {
    if (save.isPending) return
    save.reset()
    onClose()
  }

  return (
    <Dialog
      open={member !== null}
      onClose={close}
      size="md"
      title="Change roles"
      description={member ? `${member.displayName} (${member.email})` : undefined}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        <RoleCheckboxes value={roles} onChange={setRoles} allowed={allowed} idPrefix="edit-role" />
        {save.isError ? (
          <Notice tone="error">{errorMessage(save.error, 'Couldn’t change the roles')}</Notice>
        ) : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={close} disabled={save.isPending}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending || roles.length === 0}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

export function MembersPanel({ workspaceId }: { workspaceId: string }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const dialogs = useDialogs()
  const isOwner = user?.roles.includes('workspace_owner') ?? false
  const canManage = isOwner || (user?.roles.includes('administrator') ?? false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [editing, setEditing] = useState<WorkspaceMember | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [resent, setResent] = useState<CreateInvitationResponse | null>(null)

  const membersQuery = useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: () => workspaceUsersApi.list(workspaceId),
    enabled: workspaceId !== '',
  })
  const invitationsQuery = useQuery({
    queryKey: ['workspace-invitations', workspaceId],
    queryFn: () => invitationsApi.list(workspaceId),
    enabled: workspaceId !== '' && canManage,
  })

  const optionsQuery = useQuery({
    queryKey: ['workspace-invitation-options', workspaceId],
    queryFn: () => invitationsApi.options(workspaceId),
    enabled: workspaceId !== '' && canManage,
  })
  const mailEnabled = optionsQuery.data?.mailEnabled ?? false

  const removeMember = useMutation({
    mutationFn: (userId: string) => workspaceUsersApi.remove(workspaceId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] }),
    onError: (error) => setActionError(errorMessage(error, 'Couldn’t remove the member')),
  })
  const revoke = useMutation({
    mutationFn: (invitationId: string) => invitationsApi.revoke(workspaceId, invitationId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations', workspaceId] }),
    onError: (error) => setActionError(errorMessage(error, 'Couldn’t revoke the invitation')),
  })

  const resend = useMutation({
    mutationFn: (invitationId: string) => invitationsApi.resend(workspaceId, invitationId),
    onSuccess: (result) => {
      setResent(result)
      void queryClient.invalidateQueries({ queryKey: ['workspace-invitations', workspaceId] })
    },
    onError: (error) => setActionError(errorMessage(error, 'Couldn’t resend the invitation')),
  })

  async function confirmResend(invitation: InvitationSummary): Promise<void> {
    const ok = await dialogs.confirm({
      kind: 'warning',
      title: `Resend the invitation to ${invitation.email}?`,
      message: mailEnabled
        ? 'This replaces the current link with a new one and e-mails it. The old link stops working.'
        : 'This replaces the current link with a new one. The old link stops working; you share the new one yourself.',
      confirmLabel: 'Resend',
    })
    if (ok) {
      setActionError(null)
      resend.mutate(invitation.id)
    }
  }

  async function confirmRemove(member: WorkspaceMember): Promise<void> {
    const ok = await dialogs.confirm({
      kind: 'delete',
      title: `Remove ${member.displayName}?`,
      message: `${member.email} loses access to this workspace. Their account stays.`,
      confirmLabel: 'Remove',
    })
    if (ok) {
      setActionError(null)
      removeMember.mutate(member.userId)
    }
  }

  const members = membersQuery.data ?? []
  const invitations = invitationsQuery.data ?? []

  return (
    <>
      <Panel
        title="Members"
        description="Everyone with access to this workspace."
        flush
        actions={
          canManage ? (
            <button type="button" className="btn-primary btn-sm" onClick={() => setInviteOpen(true)}>
              Invite people
            </button>
          ) : undefined
        }
      >
        {actionError ? (
          <div className="p-5 pb-0">
            <Notice tone="error" onDismiss={() => setActionError(null)}>
              {actionError}
            </Notice>
          </div>
        ) : null}
        {membersQuery.isLoading ? (
          <LoadingRows />
        ) : membersQuery.isError ? (
          <ErrorNote error={membersQuery.error} what="members" />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Roles</th>
                {canManage ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.userId}>
                  <td>
                    {member.displayName}
                    {member.userId === user?.id ? (
                      <span className="ml-2 rounded-control bg-sunken px-1.5 py-0.5 text-sm text-ink-3">
                        you
                      </span>
                    ) : null}
                  </td>
                  <td>{member.email}</td>
                  <td>{member.roles.map(humanize).join(', ')}</td>
                  {canManage ? (
                    <td className="text-right">
                      {!isOwner && member.roles.includes('workspace_owner') ? null : (
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => setEditing(member)}
                        >
                          Roles
                        </button>
                        {member.userId !== user?.id ? (
                          <button
                            type="button"
                            className="btn-danger-quiet btn-sm"
                            disabled={removeMember.isPending}
                            onClick={() => void confirmRemove(member)}
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {canManage ? (
          <div className="border-t border-line p-5">
            <h3 className="font-display text-base font-semibold text-ink">Pending invitations</h3>
            {invitationsQuery.isLoading ? (
              <LoadingRows />
            ) : invitationsQuery.isError ? (
              <ErrorNote error={invitationsQuery.error} what="invitations" />
            ) : invitations.length === 0 ? (
              <p className="mt-2 text-base text-ink-3">No pending invitations.</p>
            ) : (
              <ul className="mt-3 divide-y divide-line">
                {invitations.map((invitation) => (
                  <li key={invitation.id} className="flex items-center justify-between gap-4 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-base text-ink">{invitation.email}</p>
                      <p className="text-sm text-ink-3">
                        {invitation.roles.map(humanize).join(', ')} · expires{' '}
                        {formatDateTime(invitation.expiresAt)}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={resend.isPending || revoke.isPending}
                      onClick={() => void confirmResend(invitation)}
                    >
                      {resend.isPending && resend.variables === invitation.id ? 'Resending…' : 'Resend'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={revoke.isPending || resend.isPending}
                      onClick={() => {
                        setActionError(null)
                        revoke.mutate(invitation.id)
                      }}
                    >
                      Revoke
                    </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </Panel>

      {canManage ? (
        <InviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          workspaceId={workspaceId}
          isOwner={isOwner}
          mailEnabled={mailEnabled}
        />
      ) : null}
      <Dialog
        open={resent !== null}
        onClose={() => setResent(null)}
        size="md"
        title="Invitation resent"
        description="The previous link no longer works."
      >
        {resent ? <InviteResult result={resent} onDone={() => setResent(null)} /> : null}
      </Dialog>
      <EditRolesDialog
        member={editing}
        workspaceId={workspaceId}
        isOwner={isOwner}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

function DeleteWorkspaceDialog({
  open,
  onClose,
  workspaceId,
  workspaceName,
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  workspaceName: string
}) {
  const [typed, setTyped] = useState('')
  const remove = useMutation({
    mutationFn: () => workspacesApi.remove(workspaceId, typed),
    onSuccess: (result) => switchToToken(result.token, '/'),
  })

  function close(): void {
    if (remove.isPending) return
    remove.reset()
    setTyped('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      size="md"
      title="Delete workspace"
      description="This permanently deletes the workspace with all its projects, runs and connections. It can’t be undone."
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          remove.mutate()
        }}
      >
        <Field label={`Type “${workspaceName}” to confirm`} htmlFor="delete-workspace-name">
          <input
            id="delete-workspace-name"
            data-autofocus
            className="field-input"
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setTyped(event.target.value)}
          />
        </Field>
        {remove.isError ? (
          <Notice tone="error">{errorMessage(remove.error, 'Couldn’t delete the workspace')}</Notice>
        ) : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={close} disabled={remove.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-danger"
            disabled={remove.isPending || typed !== workspaceName}
          >
            {remove.isPending ? 'Deleting…' : 'Delete workspace'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

export function LeaveOrDeletePanel({
  workspaceId,
  workspaceName,
}: {
  workspaceId: string
  workspaceName: string
}) {
  const { user } = useAuth()
  const dialogs = useDialogs()
  const isOwner = user?.roles.includes('workspace_owner') ?? false
  const [deleteOpen, setDeleteOpen] = useState(false)
  const mineQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: workspacesApi.listMine,
  })
  const onlyOne = mineQuery.data ? mineQuery.data.length <= 1 : true

  const leave = useMutation({
    mutationFn: () => workspacesApi.leave(workspaceId),
    onSuccess: (result) => switchToToken(result.token, '/'),
  })

  async function confirmLeave(): Promise<void> {
    const ok = await dialogs.confirm({
      kind: 'warning',
      title: 'Leave this workspace?',
      message: `You lose access to ${workspaceName}. An owner or administrator can invite you again.`,
      confirmLabel: 'Leave workspace',
    })
    if (ok) leave.mutate()
  }

  return (
    <Panel title="Leave or delete" className="border-danger/40">
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-base font-medium text-ink">Leave workspace</p>
            <p className="mt-0.5 text-sm text-ink-3">
              {onlyOne
                ? 'This is your only workspace. Create or join another one first.'
                : 'Remove yourself from this workspace. The last owner can’t leave.'}
            </p>
          </div>
          <button
            type="button"
            className="btn-danger-quiet shrink-0"
            disabled={onlyOne || leave.isPending}
            onClick={() => void confirmLeave()}
          >
            {leave.isPending ? 'Leaving…' : 'Leave workspace'}
          </button>
        </div>
        {leave.isError ? <Notice tone="error">{errorMessage(leave.error, 'Couldn’t leave')}</Notice> : null}
        {isOwner ? (
          <div className="flex items-start justify-between gap-4 border-t border-line pt-5">
            <div>
              <p className="text-base font-medium text-ink">Delete workspace</p>
              <p className="mt-0.5 text-sm text-ink-3">
                Permanently removes everything in it. Not possible while runs are active.
              </p>
            </div>
            <button type="button" className="btn-danger shrink-0" onClick={() => setDeleteOpen(true)}>
              Delete workspace
            </button>
          </div>
        ) : null}
      </div>
      {isOwner ? (
        <DeleteWorkspaceDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          workspaceId={workspaceId}
          workspaceName={workspaceName}
        />
      ) : null}
    </Panel>
  )
}
